import { Loading } from "../app/Status";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import {
  formatQty,
  INTAKE_CHANNEL_LABELS,
  INTAKE_REJECT_LABELS,
  INTAKE_REJECT_REASONS,
  INTAKE_REPORTER_LABELS,
  INTAKE_STATUS_LABELS,
  intakeLabel,
  isObligationChannel,
  OBLIGATION_BASIS_LABELS,
  OBLIGATION_KIND_TEXT,
  PRIO,
  priorityOf,
  Q,
  statusLabel,
  TYPES,
  type Answers,
  type IntakeItemDto,
  type IntakeListResponse,
  type IntakeRejectReason,
  type IntakeReportDto,
  type Priority,
  type BuyoutInfoDto,
} from "@klantpunt/shared";
import { Tabs } from "../app/Choice";
import { Meta } from "../app/Meta";
import { Modal } from "../app/Modal";
import { PageHeader } from "../app/PageHeader";
import { IconContract, IconEquipment, IconFile, IconInspections, IconMaterials } from "../app/icons";
import { buildingPath } from "../app/navItems";
import { buyoutParts } from "../features/contracts/useContracts";
import { Choices } from "../features/issues/IssueForm";
import { MediaThumb } from "../features/issues/MediaThumb";
import { RepliesList, useDirectoryReplyCount } from "../features/directory/Replies";
import { Logbook } from "../features/logbook/Logbook";
import { api, ApiError } from "../lib/api";
import { useModuleAccess } from "../lib/auth";
import { useToast } from "../lib/toast";

type Tab = "new" | "done" | "mail";
interface Candidate {
  id: string;
  code: string;
  title: string;
  status: string;
  priority: Priority;
  sameAsset: boolean;
}
interface Detail {
  report: IntakeReportDto;
  candidates: Candidate[];
}

export const intakeKey = ["intake"] as const;
const fmt = (iso: string) => new Date(iso).toLocaleString("nl-NL", { dateStyle: "medium", timeStyle: "short" });

/** Kop van een melding: de asset; wat er is (of welke keuring) staat eronder. */
const headline = (r: IntakeReportDto) => r.asset?.name ?? r.title;

function ResultChip({ o }: { o: NonNullable<IntakeReportDto["obligation"]> }) {
  return <span className={`chip st-res res-${o.result}`}>{OBLIGATION_KIND_TEXT[o.kind].results[o.result]}</span>;
}

/** Aantal te beoordelen voor de teller in het menu (elke minuut ververst). */
export function useIntakeCount(enabled: boolean) {
  return useQuery({
    queryKey: [...intakeKey, "new"],
    queryFn: () => api<IntakeListResponse>("/intake?status=new"),
    enabled,
    refetchInterval: 60_000,
    select: (d) => d.open,
  });
}

/**
 * Inbox *Te beoordelen* (docs/MELDINGEN.md §4): meldingen van buiten omzetten in een bevinding, koppelen aan een
 * bevinding die er al is, of afwijzen. Ook voor een servicedeskmedewerker van de klant (eigen gebouwen).
 */
export function IntakePage() {
  const { canView, canEdit } = useModuleAccess("intake");
  // Antwoorden op mails uit het adresboek (issue #155): alleen met toegang tot het adresboek.
  const directory = useModuleAccess("directory").canView;
  const replies = useDirectoryReplyCount(canView && directory);
  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const tab: Tab = tabParam === "afgehandeld" ? "done" : tabParam === "mailantwoorden" && directory ? "mail" : "new";
  const selected = tab === "mail" ? null : params.get("id");
  const setTab = (t: Tab) =>
    setParams(t === "done" ? { tab: "afgehandeld" } : t === "mail" ? { tab: "mailantwoorden" } : {}, {
      replace: true,
    });
  const select = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set("id", id);
    else next.delete("id");
    setParams(next);
  };

  const list = useQuery({
    queryKey: [...intakeKey, tab],
    queryFn: () => api<IntakeListResponse>(`/intake?status=${tab === "new" ? "new" : "alle"}`),
    enabled: canView && tab !== "mail",
    refetchInterval: 60_000,
  });
  const items = (list.data?.items ?? []).filter((r) => (tab === "new" ? r.status === "new" : r.status !== "new"));

  if (!canView) return <div className="center">Je hebt geen toegang tot de Inbox.</div>;

  return (
    <div className={`page intakepage ${selected ? "has-detail" : ""}`}>
      <PageHeader
        label="Inbox"
        title={tab === "mail" ? "Mailantwoorden" : "Te beoordelen"}
        tabs={
          <Tabs
            label="Inbox"
            value={tab}
            onChange={setTab}
            items={[
              { value: "new", label: "Te beoordelen", count: list.data?.open },
              { value: "done", label: "Afgehandeld" },
              ...(directory ? [{ value: "mail" as const, label: "Antwoorden", count: replies.data || undefined }] : []),
            ]}
          />
        }
      />
      {tab === "mail" ? (
        <div className="intake-split">
          <RepliesList />
        </div>
      ) : (
        <div className="intake-split">
          <section className="panel intake-list" aria-label="Meldingen">
            {list.isPending && <Loading />}
            {list.isError && (
              <div className="alert" role="alert">
                Meldingen konden niet worden geladen.
              </div>
            )}
            {list.data && !items.length && (
              <p className="muted">
                {tab === "new"
                  ? "Er zijn geen meldingen te beoordelen. Nieuwe meldingen via de QR-code van een asset komen hier binnen."
                  : "Nog geen afgehandelde meldingen."}
              </p>
            )}
            <ul className="intake-items">
              {items.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    className="intake-item"
                    aria-current={r.id === selected ? "true" : undefined}
                    onClick={() => select(r.id)}
                  >
                    {r.photos[0] ? (
                      <span className="intake-thumb">
                        <MediaThumb fileId={r.photos[0]} alt="" />
                      </span>
                    ) : r.document ? (
                      <span className="intake-thumb icon" title={INTAKE_CHANNEL_LABELS[r.channel]}>
                        <IconFile aria-hidden />
                      </span>
                    ) : r.material ? (
                      <span className="intake-thumb icon" title={INTAKE_CHANNEL_LABELS[r.channel]}>
                        <IconMaterials aria-hidden />
                      </span>
                    ) : r.contract ? (
                      <span className="intake-thumb icon" title={INTAKE_CHANNEL_LABELS[r.channel]}>
                        <IconContract aria-hidden />
                      </span>
                    ) : r.obligation ? (
                      <span className="intake-thumb icon" title={INTAKE_CHANNEL_LABELS[r.channel]}>
                        {r.channel === "onderhoud" ? <IconEquipment aria-hidden /> : <IconInspections aria-hidden />}
                      </span>
                    ) : (
                      <span className="intake-thumb none">geen foto</span>
                    )}
                    <span className="grow">
                      <span className="intake-title">{headline(r)}</span>
                      {(r.obligation || r.asset || r.document || r.material || r.contract) && (
                        <span className="muted small block intake-sub">
                          {r.material
                            ? `Voorraad ${formatQty(r.material.quantity)}, minimum ${formatQty(r.material.minQuantity ?? 0)}`
                            : (r.document?.typeName ??
                              r.obligation?.name ??
                              (r.contract ? `Contract ${r.contract.name}` : r.title))}
                        </span>
                      )}
                      <span className="muted small block">
                        <span className="code">{r.code}</span> · {r.building.name} · {fmt(r.createdAt)}
                      </span>
                      <span className="intake-tags">
                        {r.answers.danger === "ja" && <span className="chip prio prio-P1">gevaarlijk</span>}
                        {r.noPhoto && !isObligationChannel(r.channel) && !r.document && !r.material && !r.contract && (
                          <span className="chip">zonder foto</span>
                        )}
                        {r.obligation && <ResultChip o={r.obligation} />}
                        {r.buyout && <span className="chip">Afkoop storingen</span>}
                        {r.status === "new" ? (
                          <span className={`chip prio prio-${r.suggestedPriority}`} title="Voorgestelde prioriteit">
                            {r.suggestedPriority}
                          </span>
                        ) : (
                          <span className="chip">{INTAKE_STATUS_LABELS[r.status]}</span>
                        )}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
          {selected && <IntakeDetail key={selected} id={selected} canEdit={canEdit} onClose={() => select(null)} />}
        </div>
      )}
    </div>
  );
}

type Dialog = "convert" | "link" | "reject" | null;

function IntakeDetail({ id, canEdit, onClose }: { id: string; canEdit: boolean; onClose: () => void }) {
  const q = useQuery({ queryKey: [...intakeKey, "detail", id], queryFn: () => api<Detail>(`/intake/${id}`) });
  const [dialog, setDialog] = useState<Dialog>(null);
  const [sel, setSel] = useState<string[]>([]);
  if (q.isPending)
    return (
      <section className="panel intake-detail">
        <Loading />
      </section>
    );
  if (q.isError || !q.data)
    return (
      <section className="panel intake-detail">
        <div className="alert" role="alert">
          Deze melding bestaat niet (meer) of je hebt er geen toegang toe.
        </div>
        <button type="button" className="btn" onClick={onClose}>
          Terug
        </button>
      </section>
    );
  const r = q.data.report;
  // Melding met losse gebreken: een actie geldt voor de gekozen gebreken.
  const open = r.items.filter((i) => i.status === "new");
  const picked = r.items.length ? open.filter((i) => sel.includes(i.id)) : null;
  const needsPick = picked !== null && picked.length === 0;
  const count = picked?.length ? ` (${picked.length})` : "";
  const done = () => {
    setDialog(null);
    setSel([]);
  };
  return (
    <section className="panel intake-detail" aria-labelledby="intake-h">
      <button type="button" className="linkbtn intake-back" onClick={onClose}>
        ← Alle meldingen
      </button>
      {r.document ? (
        <DocumentBody r={r} doc={r.document} />
      ) : r.material ? (
        <MaterialBody r={r} m={r.material} />
      ) : r.contract ? (
        <ContractBody r={r} c={r.contract} />
      ) : r.obligation ? (
        <ObligationBody r={r} o={r.obligation} />
      ) : (
        <>
          <p className="label">
            <span className="code">{r.code}</span> · {fmt(r.createdAt)}
          </p>
          <h2 id="intake-h">{headline(r)}</h2>
          <Where r={r} />
          <p className="intake-chips">
            {r.asset && <span className="chip">{r.title}</span>}
            {r.answers.danger === "ja" && <span className="chip prio prio-P1">gevaarlijk</span>}
            <span className={`chip prio prio-${r.suggestedPriority}`} title="Voorgestelde prioriteit">
              {r.suggestedPriority} · {PRIO[r.suggestedPriority].l}
            </span>
          </p>
          {r.answers.danger === "ja" && r.status === "new" && (
            <div className="alert" role="alert">
              De melder geeft aan dat het gevaarlijk is.
            </div>
          )}
          {r.buyout && <BuyoutBlock b={r.buyout} />}
          {r.photos.length > 0 ? (
            <div className="photos">
              {r.photos.map((f, i) => (
                <a key={f} className="ph" href={`/api/files/${f}/url`} target="_blank" rel="noreferrer">
                  <MediaThumb fileId={f} alt={`Foto ${i + 1}`} />
                </a>
              ))}
            </div>
          ) : (
            <p className="hint warn">Zonder foto: de melder kon geen foto maken. Vraag zo nodig na of ga kijken.</p>
          )}
          {r.description && <blockquote className="intake-note">{r.description}</blockquote>}
          <p className="muted small intake-answers">
            Gevaarlijk: {intakeLabel("danger", r.answers.danger).toLowerCase()} · Nog bruikbaar:{" "}
            {intakeLabel("usable", r.answers.usable).toLowerCase()} · Sinds:{" "}
            {intakeLabel("since", r.answers.since).toLowerCase()}
          </p>
          <div className="intake-ob">
            <span className="intake-ob-kind">Melder · {INTAKE_REPORTER_LABELS[r.reporter.kind]}</span>
            <span className="intake-ob-name">{r.reporter.name ?? "Geen naam opgegeven"}</span>
            {(r.reporter.email || r.reporter.phone) && (
              <span className="small">
                {r.reporter.email && <a href={`mailto:${r.reporter.email}`}>{r.reporter.email}</a>}
                {r.reporter.email && r.reporter.phone && " · "}
                {r.reporter.phone && <a href={`tel:${r.reporter.phone}`}>{r.reporter.phone}</a>}
              </span>
            )}
            {!r.reporter.email && !r.reporter.phone && <span className="muted small">Geen contactgegevens</span>}
          </div>
          {r.status !== "new" && (
            <dl className="subinfo">
              <HandledRows r={r} />
            </dl>
          )}
        </>
      )}
      {r.items.length > 0 && <Defects r={r} canEdit={canEdit && r.status === "new"} sel={sel} onSel={setSel} />}
      {r.status === "new" && canEdit && (
        <div className="btnrow intake-actions">
          {r.document && (
            <Link className="btn primary" to={`${buildingPath(r.building.id, "dossier")}?document=${r.document.id}`}>
              Nieuwe versie in het dossier
            </Link>
          )}
          {r.material && (
            <Link className="btn primary" to={`${buildingPath(r.building.id, "materialen")}?artikel=${r.material.id}`}>
              Naar het artikel
            </Link>
          )}
          {r.contract && (
            <Link
              className="btn primary"
              to={`/contracten/${r.contract.id}${r.contract.signal === "price" ? "?tab=posten" : ""}`}
            >
              Naar het contract
            </Link>
          )}
          {!r.contract && (
            <>
              <button
                type="button"
                className={r.document || r.material ? "btn" : "btn primary"}
                disabled={needsPick}
                onClick={() => setDialog("convert")}
              >
                Bevinding maken{count}
              </button>
              <button type="button" className="btn" disabled={needsPick} onClick={() => setDialog("link")}>
                Koppelen aan bevinding{count}
              </button>
            </>
          )}
          <button type="button" className="btn ghost" disabled={needsPick} onClick={() => setDialog("reject")}>
            Afwijzen{count}
          </button>
        </div>
      )}
      <Logbook entity="intake" id={r.id} path={`/intake/${r.id}/log`} buildingId={r.building.id} />
      {dialog === "convert" && <ConvertDialog report={r} items={picked} onClose={done} />}
      {dialog === "link" && <LinkDialog report={r} items={picked} candidates={q.data.candidates} onClose={done} />}
      {dialog === "reject" && <RejectDialog report={r} items={picked} onClose={done} />}
    </section>
  );
}

/**
 * Afkoop storingen (docs/CONTRACTEN.md, C2): de asset valt onder een storingspost. Met het storingsnummer van de
 * leverancier en de teller tegen het maximum; daarboven valt een storing buiten de afkoop.
 */
function BuyoutBlock({ b }: { b: BuyoutInfoDto }) {
  const full = b.maxCallsPerYear != null && b.callsThisYear >= b.maxCallsPerYear;
  return (
    <div className="intake-ob" aria-label="Afkoop storingen">
      <span className="intake-ob-kind">
        {b.billing === "fixed" ? "Afkoop storingen" : "Storingscontract"} ·{" "}
        <Link to={`/contracten/${b.contractId}?tab=storingen`}>{b.contractName}</Link>
      </span>
      <span className="intake-ob-name">{b.supplier?.name ?? b.lineDescription}</span>
      {b.supplier?.emergencyPhone && (
        <span className="small">
          Storingsnummer <a href={`tel:${b.supplier.emergencyPhone}`}>{b.supplier.emergencyPhone}</a>
        </span>
      )}
      <span className="small muted">
        <Meta items={buyoutParts(b)} />
      </span>
      <span className="small">
        {b.callsThisYear === 1 ? "1 storing" : `${b.callsThisYear} storingen`} dit jaar
        {b.billing === "fixed" && b.maxCallsPerYear != null && ` van de ${b.maxCallsPerYear}`}
      </span>
      {full && <span className="chip tone tone-warn">Maximum bereikt: een volgende storing valt buiten de afkoop</span>}
    </div>
  );
}

/** Gebouw en asset als rustige regel onder de kop. */
function Where({ r }: { r: IntakeReportDto }) {
  return (
    <p className="intake-where">
      <Link to={buildingPath(r.building.id, "overzicht")}>{r.building.name}</Link>
      {r.asset && (
        <>
          {" · "}
          <Link to={`${buildingPath(r.building.id, "assets")}?asset=${r.asset.id}`}>Asset bekijken</Link>
        </>
      )}
    </p>
  );
}

/** Gebreken uit een keuring of onderhoud (submeldingen): kiezen om samen of apart te verwerken. */
function Defects({
  r,
  canEdit,
  sel,
  onSel,
}: {
  r: IntakeReportDto;
  canEdit: boolean;
  sel: string[];
  onSel: (ids: string[]) => void;
}) {
  const open = r.items.filter((i) => i.status === "new");
  const toggle = (id: string) => onSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  return (
    <fieldset className="intake-defects">
      <legend className="intake-ob-kind">
        Gebreken · {open.length ? `${open.length} van ${r.items.length} te beoordelen` : "alle beoordeeld"}
      </legend>
      {canEdit && open.length > 1 && (
        <p className="muted small">Kies een of meer gebreken. Samen gekozen gebreken worden één bevinding.</p>
      )}
      <ul>
        {r.items.map((i) => (
          <li key={i.id}>
            {i.status === "new" && canEdit ? (
              <label>
                <input type="checkbox" checked={sel.includes(i.id)} onChange={() => toggle(i.id)} />
                <span>{i.text}</span>
              </label>
            ) : (
              <span className={i.status === "new" ? undefined : "intake-defect-done"}>
                <span>{i.text}</span>
                <ItemOutcome r={r} i={i} />
              </span>
            )}
          </li>
        ))}
      </ul>
    </fieldset>
  );
}

function ItemOutcome({ r, i }: { r: IntakeReportDto; i: IntakeItemDto }) {
  if (i.status === "new") return null;
  return (
    <span className="muted small block">
      {i.status === "rejected"
        ? `Afgewezen${i.rejectReason ? `: ${INTAKE_REJECT_LABELS[i.rejectReason].toLowerCase()}` : ""}`
        : i.issue && (
            <>
              {i.status === "converted" ? "Bevinding " : "Gekoppeld aan "}
              <Link to={`${buildingPath(r.building.id, "bevindingen")}?bevinding=${i.issue.id}`}>
                {i.issue.code} {i.issue.title}
              </Link>
            </>
          )}
    </span>
  );
}

/** Afhandeling van een melding (tabblad Afgehandeld). */
function HandledRows({ r }: { r: IntakeReportDto }) {
  if (r.status === "new") return null;
  return (
    <>
      <dt>Afgehandeld</dt>
      <dd>
        {INTAKE_STATUS_LABELS[r.status]}
        {r.issue && (
          <>
            {": "}
            <Link to={`${buildingPath(r.building.id, "bevindingen")}?bevinding=${r.issue.id}`}>
              {r.issue.code} {r.issue.title}
            </Link>
          </>
        )}
        {r.rejectReason && `: ${INTAKE_REJECT_LABELS[r.rejectReason]}`}
        {r.rejectNote && ` (${r.rejectNote})`}
        {r.handledBy && (
          <span className="muted small block">
            door {r.handledBy.name}
            {r.handledBy.isClient && " (klant)"}
            {r.handledAt && ` · ${fmt(r.handledAt)}`}
          </span>
        )}
      </dd>
    </>
  );
}

/**
 * Melding uit een keuring of onderhoud: de asset als kop, uitslag en voorstel als chips, de opmerkingen als citaat
 * en de keuring als één blok. Geen tabel met labels: dat wordt op de telefoon een smalle kolom vol afbrekingen.
 */
/** Voorraad onder het minimum (Materialen, fase 2). Ontvangen of tellen tot boven het minimum handelt hem af. */
function MaterialBody({ r, m }: { r: IntakeReportDto; m: NonNullable<IntakeReportDto["material"]> }) {
  return (
    <>
      <p className="label">
        <span className="code">{r.code}</span> · {fmt(r.createdAt)}
      </p>
      <h2 id="intake-h">{headline(r)}</h2>
      <Where r={r} />
      <p className="intake-chips">
        <span className="chip">{INTAKE_CHANNEL_LABELS[r.channel]}</span>
        <span className={`chip prio prio-${r.suggestedPriority}`} title="Voorgestelde prioriteit">
          {r.suggestedPriority} · {PRIO[r.suggestedPriority].l}
        </span>
      </p>
      <div className="intake-ob">
        <span className="intake-ob-kind">Artikel{m.articleNo ? ` · ${m.articleNo}` : ""}</span>
        <span className="intake-ob-name">{m.name}</span>
        <span className="muted small">
          Voorraad nu {formatQty(m.quantity)} {m.unit}
          {m.minQuantity != null && ` · minimum ${formatQty(m.minQuantity)}`}
          {m.location && ` · ${m.location}`}
        </span>
      </div>
      {r.description && <p className="muted small">{r.description}</p>}
      {r.status !== "new" && (
        <dl className="subinfo">
          <HandledRows r={r} />
        </dl>
      )}
    </>
  );
}

/** Herinnering uit het dossier: document verloopt of is verlopen. Een nieuwe versie handelt hem af. */
function DocumentBody({ r, doc }: { r: IntakeReportDto; doc: NonNullable<IntakeReportDto["document"]> }) {
  return (
    <>
      <p className="label">
        <span className="code">{r.code}</span> · {fmt(r.createdAt)}
      </p>
      <h2 id="intake-h">{headline(r)}</h2>
      <Where r={r} />
      <p className="intake-chips">
        <span className="chip">{INTAKE_CHANNEL_LABELS[r.channel]}</span>
        <span className={`chip prio prio-${r.suggestedPriority}`} title="Voorgestelde prioriteit">
          {r.suggestedPriority} · {PRIO[r.suggestedPriority].l}
        </span>
      </p>
      <div className="intake-ob">
        <span className="intake-ob-kind">{doc.typeName}</span>
        <span className="intake-ob-name">{doc.title}</span>
        <span className="muted small">
          Versie {doc.version}
          {doc.validUntil &&
            ` · geldig tot ${new Date(doc.validUntil).toLocaleDateString("nl-NL", { dateStyle: "medium" })}`}
        </span>
        <a className="small" href={`/api/files/${doc.fileId}/url`} target="_blank" rel="noreferrer">
          Huidige versie openen
        </a>
      </div>
      {r.description && <p className="muted small">{r.description}</p>}
      {r.status !== "new" && (
        <dl className="subinfo">
          <HandledRows r={r} />
        </dl>
      )}
    </>
  );
}

const SIGNAL_KIND = { notice: "Opzegtermijn", end: "Einddatum", price: "Prijsindexatie" } as const;

function ContractBody({ r, c }: { r: IntakeReportDto; c: NonNullable<IntakeReportDto["contract"]> }) {
  return (
    <>
      <p className="label">
        <span className="code">{r.code}</span> · {fmt(r.createdAt)}
      </p>
      <h2 id="intake-h">{headline(r)}</h2>
      <Where r={r} />
      <p className="intake-chips">
        <span className="chip">{INTAKE_CHANNEL_LABELS[r.channel]}</span>
        {c.signal && <span className="chip">{SIGNAL_KIND[c.signal]}</span>}
      </p>
      <div className="intake-ob">
        <span className="intake-ob-kind">Contract</span>
        <span className="intake-ob-name">{c.name}</span>
      </div>
      {r.description && <p>{r.description}</p>}
      {r.status !== "new" && (
        <dl className="subinfo">
          <HandledRows r={r} />
        </dl>
      )}
    </>
  );
}

function ObligationBody({ r, o }: { r: IntakeReportDto; o: NonNullable<IntakeReportDto["obligation"]> }) {
  const kt = OBLIGATION_KIND_TEXT[o.kind];
  const performed = new Date(o.performedOn).toLocaleDateString("nl-NL", { dateStyle: "medium" });
  return (
    <>
      <p className="label">
        <span className="code">{r.code}</span> · {fmt(r.createdAt)}
      </p>
      <h2 id="intake-h">{headline(r)}</h2>
      <Where r={r} />
      <p className="intake-chips">
        <ResultChip o={o} />
        <span className={`chip prio prio-${r.suggestedPriority}`} title="Voorgestelde prioriteit">
          {r.suggestedPriority} · {PRIO[r.suggestedPriority].l}
        </span>
      </p>
      {r.description && <blockquote className="intake-note">{r.description}</blockquote>}
      <div className="intake-ob">
        <span className="intake-ob-kind">
          {kt.noun}
          {o.basis === "wettelijk" && ` · ${OBLIGATION_BASIS_LABELS[o.basis].toLowerCase()}`}
        </span>
        <span className="intake-ob-name">{o.name}</span>
        <span className="muted small">
          {performed}
          {o.performedByCompany && ` · ${o.performedByCompany}`}
        </span>
        {o.certificateFileId && (
          <a className="small" href={`/api/files/${o.certificateFileId}/url`} target="_blank" rel="noopener">
            {kt.document} openen
          </a>
        )}
      </div>
      <p className="muted small">Vastgelegd door {r.reporter.name ?? "onbekend"}</p>
      {r.status !== "new" && (
        <dl className="subinfo">
          <HandledRows r={r} />
        </dl>
      )}
    </>
  );
}

function useHandled(onClose: () => void, message: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: intakeKey });
      onClose();
      toast(message);
    },
    onError: (e: unknown) => toast(e instanceof ApiError ? e.message : "Opslaan mislukt"),
  };
}

/** Voorstel bij gekozen gebreken: één gebrek als titel, meerdere als lijst in de omschrijving. */
function itemDraft(r: IntakeReportDto, items: IntakeItemDto[] | null) {
  if (!items?.length) return { title: r.draft.title, description: r.draft.description ?? "" };
  const list = items.map((i) => `- ${i.text}`).join("\n");
  return {
    title: (items.length === 1 ? items[0]!.text : r.draft.title).slice(0, 200),
    description: [r.draft.description, `Gebreken:\n${list}`].filter(Boolean).join("\n\n"),
  };
}

function ConvertDialog({
  report: r,
  items,
  onClose,
}: {
  report: IntakeReportDto;
  items: IntakeItemDto[] | null;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(() => itemDraft(r, items).title);
  const [description, setDescription] = useState(() => itemDraft(r, items).description);
  const [a, setA] = useState<Answers>(r.draft.answers);
  const prio = priorityOf(a);
  const save = useMutation({
    mutationFn: () =>
      api<IntakeReportDto>(`/intake/${r.id}/convert`, {
        method: "POST",
        body: { issueId: crypto.randomUUID(), title, description, answers: a, itemIds: items?.map((i) => i.id) },
      }),
    ...useHandled(
      onClose,
      items && items.length > 1 ? `Bevinding gemaakt uit ${items.length} gebreken` : "Bevinding gemaakt",
    ),
  });
  const pick = (k: keyof Answers) => (v: string) => setA({ ...a, [k]: v } as Answers);
  return (
    <Modal
      title={`Bevinding maken uit ${r.code}`}
      onClose={onClose}
      busy={save.isPending}
      wide
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose} disabled={save.isPending}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={!title.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            Bevinding maken · {prio}
          </button>
        </>
      }
    >
      <label className="field">
        <span>Titel *</span>
        <input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
      </label>
      <label className="field">
        <span>Omschrijving</span>
        <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>
      {r.obligation ? (
        <p className="muted small">
          Voorgevuld uit de {OBLIGATION_KIND_TEXT[r.obligation.kind].noun.toLowerCase()}. Herkomst wordt{" "}
          <b>Controleronde</b>; de asset en zijn plek op de tekening gaan mee.
        </p>
      ) : (
        <p className="muted small">
          Voorgevuld uit de antwoorden van de melder. Herkomst wordt <b>Melding gebruiker</b>; foto&apos;s
          {r.asset ? ", de asset en zijn plek op de tekening" : ""} gaan mee.
        </p>
      )}
      <Choices label="Wat voor soort bevinding?" required value={a.type} opts={TYPES} onPick={pick("type")} />
      <Choices label={Q.danger.q} required value={a.danger} opts={Q.danger.opts} onPick={pick("danger")} />
      <Choices label={Q.impact.q} required value={a.impact} opts={Q.impact.opts} onPick={pick("impact")} />
      <Choices label={Q.worsen.q} required value={a.worsen} opts={Q.worsen.opts} onPick={pick("worsen")} />
      <p>
        Prioriteit: <span className={`chip prio prio-${prio}`}>{prio}</span> {PRIO[prio].l}
      </p>
    </Modal>
  );
}

function LinkDialog({
  report: r,
  items,
  candidates,
  onClose,
}: {
  report: IntakeReportDto;
  items: IntakeItemDto[] | null;
  candidates: Candidate[];
  onClose: () => void;
}) {
  const [issueId, setIssueId] = useState<string | null>(candidates.find((c) => c.sameAsset)?.id ?? null);
  const save = useMutation({
    mutationFn: () =>
      api<IntakeReportDto>(`/intake/${r.id}/link`, {
        method: "POST",
        body: { issueId, itemIds: items?.map((i) => i.id) },
      }),
    ...useHandled(onClose, items ? "Gebreken gekoppeld" : "Melding gekoppeld"),
  });
  return (
    <Modal
      title={
        items
          ? `${items.length === 1 ? "Gebrek" : `${items.length} gebreken`} uit ${r.code} koppelen`
          : `${r.code} koppelen aan een bevinding`
      }
      onClose={onClose}
      busy={save.isPending}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose} disabled={save.isPending}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={!issueId || save.isPending}
            onClick={() => save.mutate()}
          >
            Koppelen
          </button>
        </>
      }
    >
      <p className="muted small">
        Voor een melding die al bekend is. De bevinding krijgt een regel “nog steeds aanwezig” en de foto&apos;s.
      </p>
      {candidates.length ? (
        <fieldset className="q">
          <legend className="ql">Open bevindingen in {r.building.name}</legend>
          <ul className="intake-cands">
            {candidates.map((c) => (
              <li key={c.id}>
                <label>
                  <input type="radio" name="cand" checked={issueId === c.id} onChange={() => setIssueId(c.id)} />
                  <span className={`chip prio prio-${c.priority}`}>{c.priority}</span>
                  <span className="grow">
                    <span className="code">{c.code}</span> {c.title}
                    <span className="muted small block">
                      {statusLabel(c.status)}
                      {c.sameAsset && " · zelfde asset"}
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
      ) : (
        <p className="muted">Er zijn geen open bevindingen in dit gebouw. Maak een nieuwe bevinding.</p>
      )}
    </Modal>
  );
}

function RejectDialog({
  report: r,
  items,
  onClose,
}: {
  report: IntakeReportDto;
  items: IntakeItemDto[] | null;
  onClose: () => void;
}) {
  const [reason, setReason] = useState<IntakeRejectReason | undefined>();
  const [note, setNote] = useState("");
  const save = useMutation({
    mutationFn: () =>
      api<IntakeReportDto>(`/intake/${r.id}/reject`, {
        method: "POST",
        body: { reason, note, itemIds: items?.map((i) => i.id) },
      }),
    ...useHandled(onClose, items ? "Gebreken afgewezen" : "Melding afgewezen"),
  });
  return (
    <Modal
      title={
        items
          ? `${items.length === 1 ? "Gebrek" : `${items.length} gebreken`} uit ${r.code} afwijzen`
          : `${r.code} afwijzen`
      }
      onClose={onClose}
      busy={save.isPending}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose} disabled={save.isPending}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={!reason || save.isPending}
            onClick={() => save.mutate()}
          >
            Afwijzen
          </button>
        </>
      }
    >
      <Choices
        label="Reden"
        required
        value={reason}
        opts={INTAKE_REJECT_REASONS.map((k) => ({ k, l: INTAKE_REJECT_LABELS[k] }))}
        onPick={(k) => setReason(k as IntakeRejectReason)}
      />
      <label className="field">
        <span>Toelichting (optioneel)</span>
        <textarea rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
    </Modal>
  );
}
